<template>
  <custom-callout
    border-color="primary-2"
    class="mb-4 custom-callout rounded"
    :has-toggle="false"
  >
    <div class="py-2">
      <div class="d-flex">
        <icon-view
          class="mt-5 mb-3 mt-md-0 mt-sm-5"
          :gradient="false"
          icon="fas fa-file-invoice mb-1"
          icon-background="white"
          icon-color="#FF7058"
          icon-size="16px"
          :title="title"
        />
      </div>

      <validation-observer ref="addAssistantObserver">
        <div class="row my-3 ml-4">
          <div class="col-md-3 col-sm-12 mr-md mb-2">
            <div v-if="showManualSSNInput">
              <validation-provider
                v-slot="{ errors }"
                :rules="ssnRules"
                :custom-messages="{
                  required: 'El número de Seguro Social es requerido.',
                  completePersonalssn:
                    'El número de Seguro Social debe ser correcto.',
                }"
              >
                <custom-input
                  id="txtAssistantSSN"
                  v-model="model.socialSecurityNumber"
                  label="Número de Seguro Social:"
                  mask-type="Personalssn"
                  maxlength="11"
                  placeholder="xxx-xx-xxxx"
                  :error="ssnFieldError(errors)"
                  :custom-error-msg="ssnFieldError(errors)"
                  @input="onSSNInput"
                />
              </validation-provider>
            </div>

            <div v-if="showGeneratedSSNLabel" class="generated-ssn-wrapper">
              <label class="input-label">Número de Seguro Social:</label>
              <div class="generated-ssn-value">{{ maskedGeneratedSSN }}</div>
            </div>

            <div
              v-if="isPrivateSchool"
              class="custom-control custom-checkbox mt-2"
            >
              <input
                id="no-social-security-checkbox"
                v-model="noSocialSecuritySelection"
                type="checkbox"
                class="custom-control-input"
                @change="onNoSocialSecurityOptionChange"
              />
              <label
                class="custom-control-label custom-radio-input-label"
                for="no-social-security-checkbox"
              >
                No cuento con seguro social
              </label>
            </div>
          </div>

          <div class="col-md-3 col-sm-12 mr-md mb-2">
            <validation-provider
              v-slot="{ errors }"
              :rules="'required'"
              :custom-messages="{
                required: 'El nombre del asistente es requerido.',
              }"
            >
              <custom-input
                id="txtAssistantName"
                v-model="model.assistantName"
                label="Nombre del Asistente:"
                placeholder="Escriba el nombre"
                :error="errors[0]"
                :custom-error-msg="errors[0]"
              />
            </validation-provider>
          </div>
        </div>
        <div class="row ml-4 mt-2">
          <div
            class="col-12 d-flex justify-content-md-end justify-content-start"
          >
            <action-button
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger mr-3 pl-3 pr-4"
              @click="onClose"
            />
            <action-button
              i="fas fa-save"
              text="Guardar"
              variant="success pl-3 pr-3"
              @click="onSave"
            />
          </div>
        </div>
      </validation-observer>
    </div>
  </custom-callout>
</template>

<script>
import CustomCallout from 'custom-callout';
import ActionButton from 'ActionButton';
import IconView from 'icon-view';
import CustomInput from '@/components/common/CustomInput';
import locationService from '@/services/api/location.api';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import alert from '@/mixin/alert.mixin';

const duplicateSSNMessage =
  'El número de Seguro Social, ya esta asociado a un asistente.';

export default {
  name: 'AddAssistantSection',
  components: {
    IconView,
    ActionButton,
    CustomInput,
    CustomCallout,
    ValidationObserver,
    ValidationProvider,
  },
  mixins: [alert],
  props: {
    assistants: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      required: true,
    },
    isPublicSchool: {
      type: Boolean,
      default: true,
    },
    isSSNRequiredForAssistant: {
      type: Boolean,
      default: true,
    },
    academicYearId: {
      type: [Number, String],
      default: null,
    },
    schoolId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    model: {
      socialSecurityNumber: '',
      assistantName: '',
    },
    noSocialSecuritySelection: false,
    ssnDuplicateError: '',
    ssnErrorMessage: '',
  }),
  computed: {
    isPrivateSchool() {
      return !this.isPublicSchool;
    },
    hasNotSSN() {
      return this.isPrivateSchool && this.noSocialSecuritySelection === true;
    },
    showManualSSNInput() {
      if (!this.isPrivateSchool) return true;
      return this.noSocialSecuritySelection === false;
    },
    showGeneratedSSNLabel() {
      return this.isPrivateSchool && this.noSocialSecuritySelection === true;
    },
    maskedGeneratedSSN() {
      const normalizedSSN = this.normalizeSSN(this.model.socialSecurityNumber);
      const last4 = normalizedSSN.slice(-4).padStart(4, '#');
      return `**-***-${last4}`;
    },
    ssnRules() {
      if (!this.showManualSSNInput) return '';
      return {
        required: true,
        completePersonalssn: true,
      };
    },
  },
  watch: {
    isPublicSchool: {
      immediate: true,
      handler() {
        this.setDefaultsBySchoolType();
      },
    },
  },
  methods: {
    ssnFieldError(errors) {
      return errors[0] || this.ssnDuplicateError || this.ssnErrorMessage;
    },
    onClose() {
      this.resetForm();
      this.$emit('on-close');
    },
    normalizeSSN(value) {
      return String(value || '').replace(/\D/g, '');
    },
    getAssistantSSN(assistant) {
      return (
        assistant?.socialSecurityNumber ||
        assistant?.ssn ||
        assistant?.socialsecuritynumber ||
        assistant?.numeroSeguroSocial ||
        ''
      );
    },
    isSSNDuplicated(value) {
      const normalizedSSN = this.normalizeSSN(value);
      if (!normalizedSSN) return false;

      return this.assistants.some((assistant) => {
        const assistantSSN = this.normalizeSSN(this.getAssistantSSN(assistant));
        return assistantSSN && assistantSSN === normalizedSSN;
      });
    },
    async generateUniqueRandomSSN() {
      const { data } = await locationService.generateUniqueRandomSSN();
      return data;
    },
    setDefaultsBySchoolType() {
      if (this.isPrivateSchool) {
        this.noSocialSecuritySelection = false;
      } else {
        this.noSocialSecuritySelection = null;
      }
      this.model.socialSecurityNumber = '';
      this.ssnDuplicateError = '';
      this.ssnErrorMessage = '';
    },
    async onNoSocialSecurityOptionChange() {
      this.ssnDuplicateError = '';
      this.ssnErrorMessage = '';

      if (this.noSocialSecuritySelection) {
        await this.assignGeneratedSSN();
        return;
      }

      this.model.socialSecurityNumber = '';
    },
    async assignGeneratedSSN() {
      try {
        this.model.socialSecurityNumber = await this.generateUniqueRandomSSN();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.title ||
          e?.response?.data?.error ||
          'No se pudo generar el número de Seguro Social.';
        this.ssnErrorMessage = msg;
        return false;
      }
    },
    onSSNInput() {
      this.ssnDuplicateError = '';
      this.ssnErrorMessage = '';
    },
    toNullableInt(value) {
      if (value === null || value === undefined || value === '') return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    },
    buildSaveAssistantPayload() {
      const ssnDigits = this.normalizeSSN(this.model.socialSecurityNumber);
      return {
        SSN: ssnDigits || null,
        AssistantName: (this.model.assistantName || '').trim() || null,
        AcademicYearId: this.toNullableInt(this.academicYearId),
        SchoolId: this.toNullableInt(this.schoolId),
        HasNotSSN: this.isPrivateSchool ? this.noSocialSecuritySelection : null,
      };
    },
    async isSSNValidForAssistant() {
      const ssn = this.normalizeSSN(this.model.socialSecurityNumber);
      this.ssnErrorMessage = '';

      if (!ssn) {
        this.ssnErrorMessage = 'El número de Seguro Social es requerido.';
        return false;
      }

      try {
        const { data } = await locationService.isSSNValidForAssistant(
          encodeURIComponent(ssn),
          this.isPublicSchool,
          this.hasNotSSN
        );
        return data === true || data?.data === true || data?.isValid === true;
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.title ||
          e?.response?.data?.error ||
          'No se pudo validar el número de Seguro Social.';
        this.ssnErrorMessage = msg;
        this.ShowToast(
          'Error',
          'Favor completar la información solicitada.',
          'error'
        );
        return false;
      }
    },
    async onSave() {
      const isValid = await this.$refs.addAssistantObserver.validate();
      if (!isValid) return;

      if (this.showGeneratedSSNLabel && !this.model.socialSecurityNumber) {
        const isGenerated = await this.assignGeneratedSSN();
        if (!isGenerated) return;
      }

      if (this.isSSNDuplicated(this.model.socialSecurityNumber)) {
        this.ssnDuplicateError = duplicateSSNMessage;
        return;
      }

      const isSSNValid = await this.isSSNValidForAssistant();
      if (!isSSNValid) return;

      const payload = this.buildSaveAssistantPayload();

      try {
        await locationService.addNewAssistant(payload);
        this.ShowToast(
          '',
          'El asistente ha sido guardado exitosamente.',
          'success'
        );
        this.$emit('on-save', payload);
        this.resetForm();
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.title ||
          e?.response?.data?.error ||
          'No se pudo guardar el asistente.';
        this.ShowToast(
          'Error',
          'Favor completar la información solicitada.',
          'error'
        );
      }
    },
    resetForm() {
      this.model.assistantName = '';
      this.ssnDuplicateError = '';
      this.ssnErrorMessage = '';
      this.setDefaultsBySchoolType();
      this.$refs.addAssistantObserver?.reset();
    },
  },
};
</script>

<style scoped>
.generated-ssn-wrapper {
  display: flex;
  flex-direction: column;
}

.generated-ssn-value {
  height: 38px;
  display: flex;
  align-items: center;
  border: 1px solid #c5cdcf;
  border-radius: 0.25rem;
  padding: 0 0.75rem;
  background-color: #f8f9fa;
}

.custom-radio-input-label {
  height: 19px;
  text-align: left;
  font: Bold 16px / 21px Lato;
  letter-spacing: 0;
  color: gray;
  line-height: 19px;
}
</style>
